import MuiTextField from '@material-ui/core/TextField';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import MuiAutocomplete from '@material-ui/lab/Autocomplete';
import MuiAlert from '@material-ui/lab/Alert';
import type { Entity } from '@backstage/catalog-model';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { EntityDisplayName } from '@backstage/plugin-catalog-react';
import { ScaffolderField } from '@backstage/plugin-scaffolder-react/alpha';

export interface EntityRefAutocompleteProps {
  title: string;
  description?: string;
  required?: boolean;
  rawErrors?: string[];
  fieldId?: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  items: Entity[];
  loading: boolean;
  error: Error | undefined;
  helperText?: string;
  loadingText: string;
  errorText: string;
}

export function EntityRefAutocomplete(props: EntityRefAutocompleteProps) {
  const {
    title,
    description,
    required,
    rawErrors,
    fieldId,
    value,
    onChange,
    items,
    loading,
    error,
    helperText: hintText,
    loadingText,
    errorText,
  } = props;

  if (error) {
    return (
      <ScaffolderField
        rawDescription={description}
        rawErrors={rawErrors}
        required={required}
      >
        <MuiAlert severity="error">{errorText}</MuiAlert>
      </ScaffolderField>
    );
  }

  const selectedEntity = items.find(
    entity => stringifyEntityRef(entity) === value,
  ) ?? null;

  let helperText: string | undefined;
  if (loading) {
    helperText = loadingText;
  } else if (hintText) {
    helperText = hintText;
  }

  return (
    <ScaffolderField
      rawDescription={description}
      rawErrors={rawErrors}
      required={required}
    >
      <MuiAutocomplete
        id={fieldId}
        loading={loading}
        value={selectedEntity}
        onChange={(_event, entity) =>
          onChange(entity ? stringifyEntityRef(entity) : undefined)
        }
        options={items}
        getOptionLabel={entity =>
          entity.metadata.title ?? entity.metadata.name
        }
        renderOption={entity => (
          <EntityDisplayName entityRef={entity} />
        )}
        renderInput={params => (
          <MuiTextField
            {...params}
            label={title}
            margin="dense"
            variant="outlined"
            required={required}
            helperText={helperText}
            error={Boolean(rawErrors && rawErrors.length > 0)}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <MuiCircularProgress color="inherit" size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </ScaffolderField>
  );
}
